import { useState, useEffect, Fragment } from 'react';
import { Popover, Transition, Portal } from '@headlessui/react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

interface DatePickerProps {
  label?: string;
  value?: number | null;
  onChange: (value: number | undefined) => void;
  showTime?: boolean;
  className?: string;
  placeholder?: string;
  name?: string;
  id?: string;
}

const MONTHS = [
  '一月', '二月', '三月', '四月', '五月', '六月',
  '七月', '八月', '九月', '十月', '十一月', '十二月',
];

const DAYS_OF_WEEK = ['日', '一', '二', '三', '四', '五', '六'];

export function DatePicker({
  label,
  value,
  onChange,
  showTime = false,
  className,
  placeholder = '选择日期',
  name,
  id: customId,
}: DatePickerProps) {
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [tempDate, setTempDate] = useState<Date | null>(null);

  const selectedDate = value ? new Date(value) : null;
  const generatedId = customId || `datepicker-${label?.replace(/\s+/g, '-').toLowerCase() || 'default'}`;
  const generatedName = name || `datepicker-${label?.replace(/\s+/g, '-').toLowerCase() || 'default'}`;

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (number | null)[] = [];

    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(viewDate);
    newDate.setDate(day);

    if (showTime && tempDate) {
      newDate.setHours(tempDate.getHours());
      newDate.setMinutes(tempDate.getMinutes());
    }

    setTempDate(newDate);
    onChange(newDate.getTime());
  };

  const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
    const numValue = parseInt(value, 10);
    let newDate = tempDate || selectedDate || new Date();

    if (type === 'hour') {
      newDate.setHours(numValue);
    } else {
      newDate.setMinutes(numValue);
    }

    setTempDate(newDate);
  };

  const handleQuickSelect = (type: 'today' | 'clear') => {
    if (type === 'clear') {
      onChange(undefined);
      setTempDate(null);
    } else {
      const now = new Date();
      onChange(now.getTime());
      setTempDate(now);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(viewDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setViewDate(newDate);
  };

  const formatDisplayValue = () => {
    if (!selectedDate) return '';

    const dateStr = selectedDate.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    if (showTime) {
      const timeStr = selectedDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return `${dateStr} ${timeStr}`;
    }

    return dateStr;
  };

  const days = getDaysInMonth(viewDate);
  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={generatedId}
          className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 mb-2 px-1"
        >
          {label}
        </label>
      )}

      <Popover>
        <Popover.Button
          as="button"
          type="button"
          id={generatedId}
          name={generatedName}
          className={cn(
            'relative block w-full rounded-xl bg-white/[0.02] py-3 pl-4 pr-10 text-left text-sm text-white backdrop-blur-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
            'focus:outline-none focus:ring-0',
            'hover:bg-white/[0.08]',
            !selectedDate && 'text-white/40'
          )}
        >
          <span className={cn('block truncate', !selectedDate && 'text-white/30')}>
            {selectedDate ? formatDisplayValue() : placeholder}
          </span>
          <CalendarIcon
            className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-3 w-4 h-4 stroke-white/30"
            aria-hidden="true"
          />
        </Popover.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Portal>
            <div className="fixed z-[9999] mt-2 w-[320px]">
              <Popover.Panel>
                <div className="overflow-hidden rounded-xl bg-zinc-900/95 backdrop-blur-xl shadow-2xl shadow-black/50 shadow-inner ring-1 ring-white/5">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={() => navigateMonth('prev')}
                        className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <div className="text-sm font-medium text-white">
                        {currentYear}年 {MONTHS[currentMonth]}
                      </div>
                      <button
                        type="button"
                        onClick={() => navigateMonth('next')}
                        className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <div
                          key={day}
                          className="text-center text-[10px] font-medium text-white/30 py-1"
                        >
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                      {days.map((day, index) => {
                        if (day === null) {
                          return <div key={`empty-${index}`} className="h-9" />;
                        }

                        const isSelected = selectedDate
                          && day === selectedDate.getDate()
                          && currentMonth === selectedDate.getMonth()
                          && currentYear === selectedDate.getFullYear();

                        const isToday = (() => {
                          const today = new Date();
                          return day === today.getDate()
                            && currentMonth === today.getMonth()
                            && currentYear === today.getFullYear();
                        })();

                        return (
                          <button
                            type="button"
                            key={day}
                            onClick={() => handleDateSelect(day)}
                            className={cn(
                              'h-9 w-9 rounded-lg text-sm font-medium transition-all duration-200',
                              'hover:bg-white/10',
                              isSelected
                                ? 'bg-white text-zinc-900 font-semibold'
                                : isToday
                                  ? 'text-white border border-white/20'
                                  : 'text-white/70',
                              !isSelected && 'hover:text-white'
                            )}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>

                    {showTime && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <div className="flex items-center justify-center gap-2">
                          <div className="flex items-center bg-white/5 rounded-lg px-2 py-1">
                            <input
                              type="number"
                              min={0}
                              max={23}
                              value={tempDate?.getHours() ?? selectedDate?.getHours() ?? 0}
                              onChange={(e) => handleTimeChange('hour', e.target.value)}
                              className="w-10 bg-transparent text-center text-sm text-white focus:outline-none"
                            />
                            <span className="text-white/30 text-xs">:</span>
                            <input
                              type="number"
                              min={0}
                              max={59}
                              value={tempDate?.getMinutes() ?? selectedDate?.getMinutes() ?? 0}
                              onChange={(e) => handleTimeChange('minute', e.target.value)}
                              className="w-10 bg-transparent text-center text-sm text-white focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                      <button
                        type="button"
                        onClick={() => handleQuickSelect('clear')}
                        className="text-xs text-white/40 hover:text-white transition-colors"
                      >
                        清除
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickSelect('today')}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        返回今天
                      </button>
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </div>
          </Portal>
        </Transition>
      </Popover>
    </div>
  );
}
